<template>
  <div ref="emailContainer" class="isolated-email-content"></div>
  
  <!-- 图片查看器 -->
  <ImageViewer
    v-model:show="showImageViewer"
    :src="viewerImageSrc"
    alt="Screenshot"
  />
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue'
import ImageViewer from '@/components/common/ImageViewer.vue'

const props = defineProps({
  htmlContent: {
    type: String,
    default: ''
  },
  textContent: {
    type: String,
    default: ''
  }
})

const emailContainer = ref(null)
let shadowRoot = null

// 图片查看器状态
const showImageViewer = ref(false)
const viewerImageSrc = ref('')

// 打开图片查看器
const openImageViewer = (src) => {
  viewerImageSrc.value = src
  showImageViewer.value = true
}

// 处理HTML内容，为<a>标签添加target="_blank"
const processHtmlContent = (htmlContent) => {
  if (!htmlContent) return htmlContent
  
  // 创建临时DOM元素来解析HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContent
  
  // 查找所有<a>标签
  const links = tempDiv.querySelectorAll('a')
  
  links.forEach(link => {
    // 检查是否已经有target属性，如果没有或者不是_blank，则添加
    if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
      link.setAttribute('target', '_blank')
      // 同时添加rel="noopener noreferrer"提高安全性
      link.setAttribute('rel', 'noopener noreferrer')
    }
  })
  
  return tempDiv.innerHTML
}

// 转换 Google Drive / OneDrive 等分享卡片为友好的"点击查看"UI
const transformDriveChips = (container) => {
  // 匹配 Google Drive 分享卡片 (gmail_drive_chip)
  const driveChips = container.querySelectorAll('.gmail_drive_chip, .gmail_chip')
  driveChips.forEach(chip => {
    const link = chip.querySelector('a[href]')
    if (!link) return
    
    // 提取文件名和链接
    const href = link.getAttribute('originalsrc') || link.getAttribute('href') || ''
    const fileNameEl = chip.querySelector('span')
    const fileName = fileNameEl ? fileNameEl.textContent.trim() : '附件文件'
    
    // 判断文件类型图标
    const ext = fileName.split('.').pop().toLowerCase()
    const iconMap = { pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊', ppt: '📊', pptx: '📊', zip: '📦', rar: '📦', png: '🖼️', jpg: '🖼️', jpeg: '🖼️', gif: '🖼️', mp4: '🎬', mp3: '🎵' }
    const icon = iconMap[ext] || '📎'
    
    // 创建友好的卡片
    const card = document.createElement('div')
    card.style.cssText = 'display:flex; align-items:center; gap:10px; padding:10px 14px; margin:8px 0; background:#f0f5ff; border:1px solid #d0e0ff; border-radius:8px; max-width:400px; cursor:pointer; transition:background 0.2s;'
    card.innerHTML = `
      <span style="font-size:24px; flex-shrink:0;">${icon}</span>
      <div style="flex:1; min-width:0;">
        <div style="font-size:13px; font-weight:500; color:#1a1a1a; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;" title="${fileName}">${fileName}</div>
        <div style="font-size:11px; color:#6b7280; margin-top:2px;">点击打开查看</div>
      </div>
      <a href="${href}" target="_blank" rel="noopener noreferrer" style="flex-shrink:0; padding:4px 12px; background:#3b82f6; color:#fff; border-radius:6px; font-size:12px; text-decoration:none; white-space:nowrap;">打开</a>
    `
    card.addEventListener('mouseover', () => { card.style.background = '#e0ecff' })
    card.addEventListener('mouseout', () => { card.style.background = '#f0f5ff' })
    card.addEventListener('click', () => { window.open(href, '_blank') })
    
    chip.replaceWith(card)
  })
  
  // 匹配通用的外部文件链接图片（Google Drive thirdparty icons）
  const driveIcons = container.querySelectorAll('img[src*="drive-thirdparty.googleusercontent.com"]')
  driveIcons.forEach(img => {
    const parentLink = img.closest('a')
    if (!parentLink || parentLink.closest('[data-drive-card]')) return
    
    const href = parentLink.getAttribute('originalsrc') || parentLink.getAttribute('href') || ''
    const card = document.createElement('a')
    card.href = href
    card.target = '_blank'
    card.rel = 'noopener noreferrer'
    card.style.cssText = 'display:inline-flex; align-items:center; gap:6px; padding:4px 10px; background:#f0f5ff; border:1px solid #d0e0ff; border-radius:6px; font-size:12px; color:#3b82f6; text-decoration:none; margin:2px;'
    card.textContent = '📎 点击打开查看'
    card.setAttribute('data-drive-card', '1')
    
    parentLink.replaceWith(card)
  })
}

// 将无法访问的图片替换为友好提示
const replaceInaccessibleImages = (container) => {
  const images = container.querySelectorAll('img')
  // 已知不可访问的邮箱内部图片URL
  const inaccessiblePatterns = [
    'webmail.vip.163.com', 'webmail.163.com', 'webmail.126.com',
    'mail.qq.com/cgi-bin', 'mail.google.com/mail'
  ]
  
  images.forEach(img => {
    const src = img.getAttribute('src') || ''
    const isInaccessible = inaccessiblePatterns.some(p => src.includes(p))
    
    if (isInaccessible) {
      const placeholder = document.createElement('div')
      placeholder.style.cssText = 'display:inline-flex; align-items:center; gap:6px; padding:6px 12px; margin:4px 0; background:#f9fafb; border:1px dashed #d1d5db; border-radius:6px; color:#9ca3af; font-size:12px;'
      placeholder.textContent = '🖼️ 图片来自邮箱内部，无法在此显示'
      img.replaceWith(placeholder)
    }
  })
}

// 智能图片尺寸处理：防止大图撑满宽度，同时保留小图标和邮件模板图
const smartResizeImages = (container) => {
  // 先处理不可访问的图片
  replaceInaccessibleImages(container)
  
  const images = container.querySelectorAll('img')
  
  images.forEach(img => {
    const src = img.getAttribute('src') || ''
    const width = parseInt(img.getAttribute('width')) || 0
    const height = parseInt(img.getAttribute('height')) || 0
    
    // 豁免：小图标（宽高 ≤ 48px）
    if (width > 0 && width <= 48) return
    if (height > 0 && height <= 48) return
    
    // 豁免：外部小图标
    if (src.includes('drive-thirdparty.googleusercontent.com')) return
    if (src.includes('favicon')) return
    if (src.includes('tracking') || src.includes('pixel')) return
    
    // 豁免：邮件模板 table 布局内的图片（营销邮件）
    if (img.closest('table')) return
    
    // 对所有非豁免图片应用约束
    const applyConstraint = (imgEl) => {
      imgEl.classList.add('email-img-constrained')
      // 强制覆盖内联样式（邮件HTML常带 style="max-width:100%"）
      imgEl.style.setProperty('max-width', '480px', 'important')
      imgEl.style.setProperty('width', 'auto', 'important')
      imgEl.style.setProperty('height', 'auto', 'important')
      imgEl.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        openImageViewer(imgEl.src)
      })
    }
    
    // 如果有明确尺寸，直接判断
    if (width > 100 || height > 100) {
      applyConstraint(img)
      return
    }
    
    // 没有明确尺寸的图片，等加载后判断
    if (!width && !height) {
      // 先应用约束（防止加载时闪烁撑满）
      applyConstraint(img)
      
      // 加载后如果确认是小图则移除约束
      img.addEventListener('load', () => {
        if (img.naturalWidth <= 48 && img.naturalHeight <= 48) {
          img.classList.remove('email-img-constrained')
          img.style.cursor = ''
        }
      })
    }
    
    // 图片加载失败时显示友好提示
    img.addEventListener('error', () => {
      const placeholder = document.createElement('div')
      placeholder.style.cssText = 'display:inline-flex; align-items:center; gap:6px; padding:6px 12px; margin:4px 0; background:#fef2f2; border:1px dashed #fca5a5; border-radius:6px; color:#b91c1c; font-size:12px;'
      placeholder.textContent = '🖼️ 图片加载失败'
      img.replaceWith(placeholder)
    })
  })
}

// 创建 Shadow DOM 并渲染内容，添加基础样式
const renderEmailContent = () => {
  if (!emailContainer.value) return
  
  // 清除现有的 shadow root
  if (shadowRoot) {
    shadowRoot.innerHTML = ''
  } else {
    // 创建 shadow root
    shadowRoot = emailContainer.value.attachShadow({ mode: 'open' })
  }
  
  // 添加基础样式，为无样式的元素设置合理的默认字体大小
  const style = document.createElement('style')
  style.textContent = `
    /* 基础字体大小重置 */
    :host {
      font-size: 14px;
      line-height: 1.6;
      color: #374151;
      word-wrap: break-word;
      overflow-wrap: break-word;
      word-break: break-word;
    }
    
    /* 为常见元素设置默认字体大小和换行 */
    p, div, span, td, th, li {
      font-size: 14px;
      margin: 0;
      padding: 0;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    /* 确保所有容器元素都不会超出宽度 */
    * {
      max-width: 100%;
      box-sizing: border-box;
    }
    
    p {
      margin-bottom: 0.5em;
    }
    
    /* 标题样式 */
    h1 { font-size: 1.5em; margin: 0.67em 0; }
    h2 { font-size: 1.3em; margin: 0.75em 0; }
    h3 { font-size: 1.17em; margin: 0.83em 0; }
    h4 { font-size: 1em; margin: 1em 0; }
    h5 { font-size: 0.83em; margin: 1.5em 0; }
    h6 { font-size: 0.75em; margin: 1.67em 0; }
    
    /* 链接样式 */
    a {
      color: #2563eb;
      text-decoration: underline;
    }
    
    a:hover {
      color: #1d4ed8;
    }
    
    /* 列表样式 */
    ul, ol {
      margin: 0.5em 0;
      padding-left: 2em;
    }
    
    /* 表格样式 */
    table {
      border-collapse: collapse;
      font-size: 14px;
      max-width: 100%;
      table-layout: fixed;
      width: 100%;
    }
    
    td, th {
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
    
    /* 代码样式 */
    code, pre {
      font-family: monospace;
      font-size: 13px;
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
    
    /* 图片自适应 */
    img {
      max-width: 100%;
      height: auto;
    }
    
    /* 智能图片尺寸约束 */
    img.email-img-constrained {
      max-width: 480px !important;
      width: auto !important;
      height: auto !important;
      border-radius: 6px;
      cursor: zoom-in;
      transition: box-shadow 0.2s;
    }
    
    img.email-img-constrained:hover {
      box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    }
  `
  shadowRoot.appendChild(style)
  
  // 渲染内容
  if (props.htmlContent) {
    // 处理HTML内容，为链接添加target="_blank"
    const processedHtml = processHtmlContent(props.htmlContent)
    const contentDiv = document.createElement('div')
    contentDiv.innerHTML = processedHtml
    
    // 🚀 转换 Google Drive 分享卡片为友好的"点击查看"卡片（在DOM上操作以保留事件）
    transformDriveChips(contentDiv)
    
    // 🚀 智能图片尺寸处理：防止大图擑满宽度
    smartResizeImages(contentDiv)
    
    shadowRoot.appendChild(contentDiv)
  } else if (props.textContent) {
    // 对于纯文本，直接显示
    const textDiv = document.createElement('pre')
    textDiv.textContent = props.textContent
    shadowRoot.appendChild(textDiv)
  } else {
    // 无内容时显示提示
    const emptyDiv = document.createElement('div')
    emptyDiv.textContent = '无邮件内容'
    shadowRoot.appendChild(emptyDiv)
  }
}

// 监听内容变化
watch([() => props.htmlContent, () => props.textContent], () => {
  renderEmailContent()
}, { immediate: false })

// 组件挂载后渲染
onMounted(() => {
  renderEmailContent()
})

// 组件卸载时清理
onUnmounted(() => {
  if (shadowRoot) {
    shadowRoot.innerHTML = ''
    shadowRoot = null
  }
})
</script>

<style scoped>
.isolated-email-content {
  width: 100%;
  min-height: 100px;
}
</style>
